// Formateadores puros de la sección Indicadores.
//
// REGLA DURA: acá no se construye ni un solo `Date`, ni se llama a
// `toLocaleTimeString`/`Intl`. El backend ya devuelve la hora local del
// comercio resuelta ("HH:mm" y minuto del día), así que cualquier conversión
// de este lado la rompería. Además, `new Date("2026-08-01")` se parsea como
// UTC: en zonas negativas (Chile/Argentina) renderiza el día ANTERIOR, que es
// exactamente el bug de hidratación con el que este repo ya se quemó.
//
// Todo acá es aritmética entera y partición de strings: mismo resultado en el
// servidor, en el navegador, con cualquier locale y con cualquier versión de
// ICU.

export const EMPTY_VALUE = "—";

const pad2 = (n) => String(n).padStart(2, "0");

/**
 * Minuto del día (0..1439) → "HH:mm".
 * @param {number|null|undefined} minutes
 * @returns {string} "HH:mm" o "—" si no hay dato.
 */
export const minutesToHHmm = (minutes) => {
  const value = Number(minutes);
  if (minutes === null || minutes === undefined || Number.isNaN(value)) return EMPTY_VALUE;
  const total = Math.max(0, Math.round(value));
  return `${pad2(Math.floor(total / 60))}:${pad2(total % 60)}`;
};

/**
 * Hora "HH:mm" que ya viene resuelta del backend. Se pasa tal cual; solo
 * resuelve el caso nulo. Existe como función para que la vista nunca tenga la
 * tentación de "formatear" una hora que ya viene formateada.
 * @param {string|null|undefined} hhmm
 * @returns {string}
 */
export const formatLocalTime = (hhmm) => (hhmm ? String(hhmm) : EMPTY_VALUE);

// Solo se reformatea lo que REALMENTE tiene forma de fecha ISO. Sin esta
// validación, un `split("-")` sobre cualquier string con guiones devolvería
// basura reordenada en vez del texto original.
const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * "YYYY-MM-DD" → "DD/MM/YYYY" por partición de string.
 * @param {string|null|undefined} ymd
 * @returns {string} la fecha formateada, o la entrada intacta si no es ISO.
 */
export const formatYmd = (ymd) => {
  if (typeof ymd !== "string") return EMPTY_VALUE;
  const match = YMD_RE.exec(ymd);
  if (!match) return ymd;
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
};

/**
 * "YYYY-MM-DD" → "DD/MM" (etiquetas de eje, donde el año sobra).
 * @param {string|null|undefined} ymd
 * @returns {string}
 */
export const formatYmdShort = (ymd) => {
  if (typeof ymd !== "string") return "";
  const match = YMD_RE.exec(ymd);
  if (!match) return ymd;
  const [, , month, day] = match;
  return `${day}/${month}`;
};

/**
 * Redondea a un decimal y elimina el ".0" sobrante ("83" en vez de "83.0").
 * @param {number} value
 * @returns {number}
 */
const round1 = (value) => Math.round(value * 10) / 10;

/**
 * Porcentaje 0..100 → "83%" / "83.4%". Null-safe.
 * @param {number|null|undefined} pct
 * @returns {string}
 */
export const formatPct = (pct) => {
  const value = Number(pct);
  if (pct === null || pct === undefined || Number.isNaN(value)) return EMPTY_VALUE;
  return `${round1(value)}%`;
};

/**
 * Horas decimales → "26.5 h". Null-safe.
 * @param {number|null|undefined} hours
 * @returns {string}
 */
export const formatHours = (hours) => {
  const value = Number(hours);
  if (hours === null || hours === undefined || Number.isNaN(value)) return EMPTY_VALUE;
  return `${round1(value)} h`;
};

/**
 * Entero con separador de miles del idioma activo. Es formateo de NÚMERO, no
 * de fecha: no toca zonas horarias ni calendarios.
 * @param {number|null|undefined} count
 * @returns {string}
 */
export const formatCount = (count) => {
  const value = Number(count);
  if (count === null || count === undefined || Number.isNaN(value)) return "0";
  return value.toLocaleString();
};

/**
 * Etiqueta visible de un operador.
 *
 * El backend deja `name` VACÍO para las claves especiales "own_fleet" y
 * "unknown": la etiqueta la pone el front para que sea traducible. Para el
 * resto, `name` es el nombre congelado en el paquete al crearlo y se respeta
 * tal cual (traducirlo sería inventar).
 *
 * @param {{ key?: string, operatorId?: string|null, name?: string, isOwnFleet?: boolean }} operator
 * @param {(key: string) => string} t función de traducción de react-i18next
 * @returns {string}
 */
export const resolveOperatorName = (operator, t) => {
  const name = typeof operator?.name === "string" ? operator.name.trim() : "";
  if (name) return name;
  if (operator?.isOwnFleet || operator?.key === "own_fleet") return t("Flota propia");
  return t("Sin operador");
};
